import { useMutation } from '@tanstack/react-query';

import {
  useInvalidateChannelsList,
  useOptimisticUpdateChannelById,
} from '@/entities/channel';
import { ApiError } from '@/shared/api/ApiError';
import { IChannelDetailsDto, qrLoginChannel } from '@/shared/api/channels';
import { useNotifications } from '@/shared/ui/Notifications';

type ChannelLoginOptions = {
  onSuccess?: (data: IChannelDetailsDto) => void;
  silent?: boolean;
};

export function useLoginChannelByQr() {
  const invalidateChannelsList = useInvalidateChannelsList();
  const optimisticUpdateChannelById = useOptimisticUpdateChannelById();
  const { addNotification } = useNotifications();

  const channelLoginMutation = useMutation({
    mutationFn: (channelId: string) => {
      return qrLoginChannel(channelId);
    },
    async onSuccess(data) {
      optimisticUpdateChannelById(data);
      await Promise.all([invalidateChannelsList()]);
    },
  });

  const channelLogin = (channelId: string, options?: ChannelLoginOptions) => {
    const onSuccess = options?.onSuccess;
    const silent = options?.silent ?? false;

    channelLoginMutation.mutate(channelId, {
      onSuccess: (data) => {
        onSuccess?.(data);
      },
      onError: (error) => {
        if (silent || ApiError.isCanceledError(error)) return;

        addNotification({
          title: error.message,
          caption: error?.data?.details,
        });
      },
    });
  };

  return {
    channelLogin,
    ...channelLoginMutation,
  };
}
